import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ArrowRight } from "lucide-react";
import BlogCard from "@/components/BlogCard";
import { useLang } from "@/lib/lang-context";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Ti può interessare" — homepage section surfacing the 3 most recent
 * published blog articles. Auto-hides when there's nothing to show so we
 * never render a sad empty rail to first-time visitors.
 */
const RelatedBlog = () => {
  const { lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/blog/posts`, {
          params: { limit: 6, include_scheduled: false, lang },
        });
        if (!cancelled) {
          const published = (data?.items || []).filter((p) => p.has_body).slice(0, 3);
          setPosts(published);
        }
      } catch (e) {
        // Non-fatal: section just stays hidden if the API errors.
        // eslint-disable-next-line no-console
        console.error("[related-blog] fetch failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  if (posts.length === 0) return null;

  return (
    <section
      data-testid="home-related-blog"
      className="bg-[#F0EFEB] border-t border-b border-[#E6E4DD] py-16"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="label-eyebrow inline-flex items-center gap-2 text-[#4A5D23]">
              <BookOpen size={11} strokeWidth={1.75} />
              {lang === "it" ? "Dal blog" : "From the blog"}
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#282C2A] leading-tight mt-2">
              {lang === "it" ? "Ti può interessare" : "You may also like"}
            </h2>
          </div>
          <Link
            to="/blog"
            data-testid="home-related-blog-all"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#282C2A] hover:text-[#4A5D23] border-b border-[#282C2A] hover:border-[#4A5D23] pb-0.5"
          >
            {lang === "it" ? "Tutti gli articoli" : "All articles"}
            <ArrowRight size={13} strokeWidth={1.75} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <BlogCard key={p.slug} post={p} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedBlog;
